import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dailyExerciseApi } from "../store/services";
import { showNotification } from "../store/reducers";
import { useAppDispatch } from "./store";
import { StageActionEnum } from "../shared";

interface IUseProcessStageActionProps {
  isPatient: boolean;
  patientId: string;
  pendingToChooseStageAction: boolean;
}

export const useProcessStageAction = ({
  isPatient = false,
  patientId,
  pendingToChooseStageAction,
}: IUseProcessStageActionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [openConfirmReturnToStart, setOpenConfirmReturnToStart] =
    useState(false);

  useEffect(() => {
    if (pendingToChooseStageAction) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setOpenConfirmReturnToStart(false);
    }
  }, [pendingToChooseStageAction]);

  const [processStageAction, { isLoading }] =
    dailyExerciseApi.useProcessStageActionByStaffMutation();

  const handleError = (error: any) => {
    dispatch(
      showNotification({
        type: "error",
        message: t([
          `alert.errors.${error.data.message}`,
          `alert.somethingWentWrong`,
        ]),
      }),
    );
  };

  const onHandleContinueExercises = async () => {
    try {
      await processStageAction({
        patientId,
        action: StageActionEnum.none,
      }).unwrap();
      dispatch(
        showNotification({
          type: "success",
          message: t("alert.continueExercisesSuccess"),
        }),
      );
      setIsOpen(false);
    } catch (error: any) {
      handleError(error);
    }
  };

  const onHandleConfirm = async () => {
    try {
      await processStageAction({
        patientId,
        action: StageActionEnum.reset,
      }).unwrap();
      dispatch(
        showNotification({
          type: "success",
          message: t("alert.returnToStartSuccess"),
        }),
      );
      setIsOpen(false);
      setOpenConfirmReturnToStart(false);
    } catch (error: any) {
      handleError(error);
    }
  };

  return {
    isOpen,
    openConfirmReturnToStart,
    setOpenConfirmReturnToStart,
    isLoading,
    onHandleContinueExercises,
    onHandleConfirm,
  };
};
